import { useMyUser } from '@consigli/hooks';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentChatLogo } from '@/components/agent-chat-logo';
import { Message } from '@/util/interfaces';

import { MarkdownBody } from './markdown-body';
import { ChatMessage } from './prop-chat-message';
import { type PropChatVariant } from './types';

interface ChatMessagesProps {
  loading: boolean;
  loadingMessage: boolean;
  disabled: boolean;
  messages: Message[];
  variant: PropChatVariant;
}

const InitialChatLoading: FC = () => {
  const { t } = useTranslation();

  return (
    // The visible copy is the announcement, so no aria-label to override it.
    // No page padding of its own: the frame's belongs to the scroll container,
    // which already supplies it - doubling it wrapped the title.
    <div role="status" className="flex w-full flex-col items-center justify-center">
      <div className="flex w-full flex-col items-center gap-[var(--ds-space-4)] overflow-hidden rounded-[var(--ds-radius-16)] p-[var(--ds-space-10)] text-center">
        <AgentChatLogo spinning className="size-[var(--ds-space-10)]" />
        <div className="flex w-full flex-col items-center justify-center gap-[var(--ds-space-1)]">
          <h2 className="m-0 w-full font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-large-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-large-line-height)] tracking-[var(--ds-typography-paragraph-large-letter-spacing)] text-[var(--ds-color-text-primary)]">
            {t('prop-chat.initialising-title')}
          </h2>
          <p className="m-0 font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-13-regular-font-weight)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)] text-[var(--ds-color-raw-stone-600)]">
            {t('prop-chat.initialising-description')}
          </p>
        </div>
      </div>
    </div>
  );
};

const DisabledChat: FC = () => {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-label={t('prop-chat.disabled-title')}
      className="flex w-full flex-col items-center overflow-hidden rounded-[var(--ds-radius-16)] bg-[var(--ds-color-bg-secondary)] p-[var(--ds-space-10)]"
    >
      <div className="flex w-full flex-col items-center gap-[var(--ds-space-4)] text-center">
        <AgentChatLogo className="size-[var(--ds-space-10)]" />
        <div className="flex w-full flex-col gap-[var(--ds-space-2)]">
          <h2 className="font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-large-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-large-line-height)] tracking-[var(--ds-typography-paragraph-large-letter-spacing)] text-[var(--ds-color-text-primary)]">
            {t('prop-chat.disabled-title')}
          </h2>
          <p className="font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-small-font-size)] font-[var(--ds-typography-paragraph-paragraph-weight)] leading-[var(--ds-typography-paragraph-small-line-height)] tracking-[var(--ds-typography-paragraph-small-letter-spacing)] text-[var(--ds-color-text-secondary)]">
            {t('prop-chat.disabled')}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * The single owner of Agent Chat announcements.
 *
 * The conversation log itself is not a live region: the markdown body is
 * re-rendered on every streamed character, so a live log announces the whole
 * answer over and over. This region is always in the DOM — the reliable way to
 * have a live region picked up — and holds one stable string per phase, so a
 * streamed answer produces exactly two announcements: that the agent is
 * working, then the finished answer.
 *
 * The answer is rendered rather than read from the raw body so markdown syntax
 * is not spoken, and only the most recent one is kept, so at most one message
 * is ever duplicated for a screen reader browsing the log.
 */
const ChatAnnouncement: FC<{ answer: string; isAnswering: boolean }> = ({
  answer,
  isAnswering,
}) => {
  const { t } = useTranslation();

  return (
    <div className="sr-only" role="status">
      {isAnswering ? t('prop-chat.thinking') : answer ? <MarkdownBody content={answer} /> : null}
    </div>
  );
};

export const ChatMessages: FC<ChatMessagesProps> = ({
  disabled,
  loading,
  loadingMessage,
  messages,
  variant,
}) => {
  const { user } = useMyUser();
  const lastMessage = messages[messages.length - 1];
  const completedAnswer =
    !loading && !loadingMessage && lastMessage && lastMessage.authorId !== user.id
      ? lastMessage.body
      : '';

  if (loading) {
    return <InitialChatLoading />;
  } else if (disabled) {
    return <DisabledChat />;
  } else {
    return (
      <div
        className={`flex flex-col ${
          variant === 'fullscreen' ? 'gap-[var(--ds-space-6)]' : 'gap-[var(--ds-space-4)]'
        }`}
      >
        <ChatAnnouncement answer={completedAnswer} isAnswering={loadingMessage} />
        {messages.map((message, index) => {
          if (message.authorId === user.id) {
            if (index === messages.length - 1 && loadingMessage) {
              return (
                <Fragment key={message.id}>
                  <ChatMessage variant={variant} message={message} />
                  <ChatMessage variant={variant} loading message={message} from="SYSTEM" />
                </Fragment>
              );
            } else {
              return <ChatMessage variant={variant} key={message.id} message={message} />;
            }
          } else {
            return (
              <ChatMessage
                variant={variant}
                key={message.id}
                message={message}
                // Only the newest agent message can be the one being streamed;
                // matching on an empty body alone would put a second skeleton on
                // any earlier blank answer.
                loading={loadingMessage && index === messages.length - 1 && message.body === ''}
                from="SYSTEM"
              />
            );
          }
        })}
      </div>
    );
  }
};
